import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import FaClose from 'react-icons/lib/fa/close';
import { CSSTransition } from 'react-transition-group';

import {
    capitalize,
    theme,
} from 'wanderu-css';
import { localize } from 'wanderu-i18n';

import ElementQuery from '../ElementQuery/ElementQuery';

import styles from './Toast.scss';
import mediaQueries from 'wanderu-css/mediaQueries.scss';

class Toast extends React.Component {
    static propTypes = {
        className: PropTypes.string,
        breakpoint: PropTypes.number,
        'data-id': PropTypes.string,
        onHide: PropTypes.func,
        anchorOrigin: PropTypes.shape({
            horizontal: PropTypes.oneOf(['center', 'left']).isRequired,
            vertical: PropTypes.oneOf(['bottom']).isRequired,
        }),
        autoHideDuration: PropTypes.number,
        autoHide: PropTypes.bool,
        showInline: PropTypes.bool,
        transitionDuration: PropTypes.number,
    };

    static defaultProps = {
        className: '',
        // maxWidthSmallScreen isn't a class but it is exported
        // eslint-disable-next-line css-modules/no-undef-class
        anchorOrigin: {
            horizontal: 'left',
            vertical: 'bottom',
        },
        breakpoint: parseInt(mediaQueries.maxWidthSmallScreen, 10),
        'data-id': '',
        onHide() {},
        autoHideDuration: 3000,
        autoHide: false,
        showInline: false,
        transitionDuration: theme.transitions.durations.standard,
    };

    constructor(props) {
        super(props);

        this.parentEl = null;
        this.el = React.createRef();
        this.queryElement = typeof window !== 'undefined'
            ? document.body
            : null;
        this.state = {
            isMobile: false,
            show: true,
        };
    }

    onElementQueryChange = (queryResults) => {
        this.setState(() => {
            return queryResults;
        });
    }

    componentDidMount() {
        this.parentEl = this.el.current.parentElement;

        const viewportOffset = this.parentEl.getBoundingClientRect();
        this.parentHeight = viewportOffset.height;

        if (this.props.autoHide) {
            this._timer = window.setTimeout(
                () => this.setState(() => ({ show: false })),
                this.props.autoHideDuration,
            );
        }
    }

    componentDidUpdate() {
        if (this.state.isMobile) {
            window.addEventListener('scroll', this._onScroll);
        }
    }

    componentWillUnmount() {
        if (this.state.isMobile) {
            window.addEventListener('scroll', this._onScroll);
        }

        if (this._timer) {
            window.clearTimeout(this._timer);
        }
    }

    _onScroll = (e) => {
        const { showInline } = this.props;
        const { isMobile } = this.state;

        if (!isMobile || showInline) {
            return;
        }

        const viewportOffset = this.parentEl.getBoundingClientRect();
        const yLocation = viewportOffset.top;

        // hide the toast after the user scrolls down more than 15%
        if (Math.abs(yLocation / this.parentHeight) < 0.15) {
            return;
        }

        this._onHide(e);
    }

    _onHide = (e) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }

        this.setState(() => ({ show: false }));
    }

    _onExited = () => {
        this.props.onHide();
    }

    render() {
        const {
            anchorOrigin: { vertical, horizontal },
            t,
            children,
            className,
            breakpoint,
            'data-id': dataId,
            autoHide,
            transitionDuration,
            showInline,
        } = this.props;
        const queries = {
            isMobile: {
                maxWidth: breakpoint,
            },
        };

        return (
            <CSSTransition
                classNames={{
                    appear: styles.appear,
                    enter: styles.enter,
                    enterActive: styles.enterActive,
                    enterDone: styles.enterDone,
                    exit: styles.exit,
                    exitActive: styles.exitActive,
                    exitDone: styles.exitDone,
                }}
                in={this.state.show}
                onExited={this._onExited}
                timeout={transitionDuration}
                appear
            >
                <div
                    className={classNames(
                        styles[`anchorOrigin${capitalize(vertical)}${capitalize(horizontal)}`],
                        className,
                        { [styles.toast]: !showInline },
                    )}
                    data-id={dataId}
                    ref={this.el}
                    style={{ transitionDuration: `${transitionDuration}ms` }}
                >

                    <ElementQuery
                        element={this.queryElement}
                        onChange={this.onElementQueryChange}
                        passAsProps={false}
                        queries={queries}
                    />
                    <div
                        className={classNames(
                            { [styles.toastContent]: !showInline },
                        )}
                    >
                        {children}
                    </div>
                    {!autoHide && (
                        <span
                            className={styles.closeButton}
                            onClick={this._onHide}
                            role="button"
                            title={t('close')}
                        >
                            <FaClose />
                        </span>
                    )}
                </div>
            </CSSTransition>
        );
    }
}

export {
    Toast,
};
export default localize(Toast, 'baseApp');
